/**
 * localDataSecurityService
 *
 * Purge de datos locales no autorizados para el usuario actual.
 *
 * Modelo de seguridad (resumen):
 *  - La barrera autoritativa es la RLS de Supabase. El cliente NO puede leer
 *    ni escribir fuera de su membership, haga o no purge local.
 *  - IndexedDB es una caché SIN cifrar. Este purge es HIGIENE / defense-in-depth:
 *    quita de la base local los registros de empresas a las que el usuario ya
 *    no pertenece, para que no queden "en reposo" accesibles desde devtools
 *    ni se muestren si el filtrado en lectura fallara.
 *
 * El borrado es SILENT (bulkDeleteSilent): solo emite eventos locales para
 * refrescar la UI, NUNCA dispara un BULK_DELETE hacia Supabase. Así el purge
 * local no puede borrar datos en el servidor.
 *
 * Decisiones:
 *  - Borrado quirúrgico: conserva caché de empresas a las que el usuario sí
 *    pertenece; borra solo las no-autorizadas.
 *  - Offline-friendly: si `allowedEmpresaIds` es vacío (no se pudo validar
 *    membership porque no hay red), se aborta sin borrar nada.
 */

/**
 * Tipo estructural mínimo que necesitan los repos para el purge.
 * Evita acoplarse a la clase concreta BaseRepository (los repos se obtienen
 * vía sus interfaces desde getRepositories()).
 */
interface IEmpresaScopedRepo {
  deleteWhereEmpresaNotInSilent(allowedEmpresaIds: string[]): Promise<number>;
}

export interface PurgeResult {
  /** Total de registros borrados en todas las tablas. */
  purged: number;
  /** True si el purge se omitió (sin membership para validar). */
  skipped: boolean;
}

/**
 * Ejecuta el purge de datos locales de empresas no autorizadas.
 *
 * @param allowedEmpresaIds empresas a las que el usuario pertenece activamente.
 *   Si viene vacío, se omite el purge SALVO que `opts.allowPurgeAll` sea true
 *   (caso en que el server confirmó 0 empresas autorizadas => suspensión total:
 *   se purga TODA la caché empresa-scoped).
 * @param opts.allowPurgeAll permite purgar todo cuando allowedEmpresaIds es
 *   vacío. Solo debe usarse cuando se CONFIRMÓ server-side que el usuario no
 *   tiene ninguna empresa (online), nunca por un error de red.
 */
export async function purgeUnauthorizedLocalData(
  allowedEmpresaIds: string[],
  opts: { allowPurgeAll?: boolean } = {}
): Promise<PurgeResult> {
  if (!allowedEmpresaIds || allowedEmpresaIds.length === 0) {
    if (!opts.allowPurgeAll) {
      console.log('[localDataSecurity] Purge omitido: sin membership para validar.');
      return { purged: 0, skipped: true };
    }
    // allowPurgeAll: el server confirmó 0 empresas autorizadas. Se purga todo.
    console.log('[localDataSecurity] Purge TOTAL: 0 empresas autorizadas confirmadas por el server.');
  }

  const repos = await collectRepos();
  const allowed = allowedEmpresaIds;

  let purged = 0;
  // Ejecutamos todas las tablas en paralelo: son independientes entre sí.
  const results = await Promise.all(
    repos.map(async ({ name, repo }) => {
      try {
        const n = await repo.deleteWhereEmpresaNotInSilent(allowed);
        if (n > 0) console.log(`[localDataSecurity] ${name}: ${n} registro(s) purgado(s).`);
        return n;
      } catch (err) {
        // Una tabla que falle no debe abortar el resto del purge.
        console.warn(`[localDataSecurity] Error purgando ${name}:`, err);
        return 0;
      }
    })
  );
  purged = results.reduce((a, b) => a + b, 0);

  console.log(
    `[localDataSecurity] Purge completo: ${purged} registro(s) eliminado(s) de empresas no autorizadas.`
  );
  return { purged, skipped: false };
}

/**
 * Recopila los repositorios con `empresa_id` sujetos a purge.
 * Importa `getRepositories` de forma diferida para evitar dependencias
 * circulares en el grafo de módulos (mismo patrón que AnimalRepository).
 *
 * No incluye `empresas` (maestra) ni `profile` (sesión): esos no se purgan
 * por empresa, se gestionan aparte.
 */
async function collectRepos(): Promise<{ name: string; repo: IEmpresaScopedRepo }[]> {
  const { getRepositories } = await import('../storage/DatabaseFactory');
  const r = getRepositories();
  return [
    { name: 'animales', repo: r.animal },
    { name: 'animales_historico', repo: r.animalHistorico },
    { name: 'ubicaciones', repo: r.ubicacion },
    { name: 'unidades', repo: r.unidad },
    { name: 'gastos', repo: r.gasto },
    { name: 'ingresos', repo: r.ingreso },
    { name: 'stocks', repo: r.stock },
    { name: 'stocks_historico', repo: r.stockHistorico },
    { name: 'categorias_cache', repo: r.categoriaCache },
    { name: 'notas', repo: r.nota },
    { name: 'nota_anclas', repo: r.notaAncla },
    { name: 'personas_campo', repo: r.personaCampo },
    { name: 'eventos', repo: r.evento },
    { name: 'recordatorios', repo: r.recordatorio },
    { name: 'operacion_compra_venta_animales', repo: r.operacionCompraVenta },
    // Apodos de sólo lectura (2026-10-18): sólo existen en la web.
    ...(r.apodo ? [{ name: 'apodos', repo: r.apodo as unknown as IEmpresaScopedRepo }] : []),
  ];
}
